import { useCallback, useEffect, useRef, useState } from "react";
import { Paperclip, Search, FolderPlus, Upload, Loader2, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { SfFolderList } from "../../components/allegatistudio/SfFolderList";
import { SfFileCard } from "../../components/allegatistudio/SfFileCard";
import { SfPreviewDialog } from "../../components/allegatistudio/SfPreviewDialog";
import { SfRenameDialog, SfConfirmDialog, SfUploadProgress } from "../../components/allegatistudio/SfDialogs";
import { useStoredWidth, ResizeHandle } from "../../components/ResizeHandle";

const MAX = 60 * 1024 * 1024;
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export default function AllegatiStudio() {
  const [sideW, setSideW] = useStoredWidth("pgp_w_allegati_studio", 250, 160, 480);
  const { user } = useAuth();
  const lvl = user?.permissions?.allegati_studio;
  const canWrite = ["titolare", "amministratore"].includes(user?.role) || lvl === "write" || lvl === true;
  const [folders, setFolders] = useState([]);
  const [rootCount, setRootCount] = useState(0);
  const [current, setCurrent] = useState("");
  const [q, setQ] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newFolder, setNewFolder] = useState("");
  const [uploads, setUploads] = useState([]);
  const [renameQueue, setRenameQueue] = useState([]);
  const [renaming, setRenaming] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef(null);
  const [view, setViewRaw] = useState(() => localStorage.getItem("pgp_sf_view") === "list" ? "list" : "grid");
  const setView = (v) => { setViewRaw(v); localStorage.setItem("pgp_sf_view", v); };

  const loadFolders = useCallback(() => api.get("/studio/allegati-studio/folders").then(({ data }) => { setFolders(data.folders); setRootCount(data.root_count); }).catch(err), []);
  const loadFiles = useCallback(() => {
    setLoading(true);
    return api.get("/studio/allegati-studio/files", { params: { folder_id: current, q } }).then(({ data }) => setFiles(data.files)).catch(err).finally(() => setLoading(false));
  }, [current, q]);
  const reload = () => { loadFolders(); loadFiles(); };
  useEffect(() => { loadFolders(); }, [loadFolders]);
  useEffect(() => { const t = setTimeout(loadFiles, q ? 300 : 0); return () => clearTimeout(t); }, [loadFiles, q]);

  const createFolder = async () => {
    if (!newFolder.trim()) return;
    try { await api.post("/studio/allegati-studio/folders", { name: newFolder.trim() }); setNewFolder(""); loadFolders(); } catch (e) { err(e); }
  };
  const renameFolder = (id, name) => api.patch(`/studio/allegati-studio/folders/${id}`, { name }).then(loadFolders).catch(err);
  const reorder = (ids) => {
    setFolders((fs) => ids.map((i) => fs.find((f) => f.id === i)));
    api.put("/studio/allegati-studio/folders/order", { ids }).catch((e) => { err(e); loadFolders(); });
  };

  const uploadAll = async (list) => {
    const target = current;
    const items = list.map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, pct: 0, status: "uploading" }));
    setUploads((u) => [...u, ...items]);
    const patch = (key, p) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, ...p } : x)));
    const done = [];
    for (let i = 0; i < list.length; i++) {
      const f = list[i], key = items[i].key;
      if (f.size > MAX) { patch(key, { status: "error", error: "Oltre 60 MB" }); continue; }
      const fd = new FormData(); fd.append("file", f); fd.append("folder_id", target);
      try {
        const { data } = await api.post("/studio/allegati-studio/files", fd, { headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => e.total && patch(key, { pct: Math.round((e.loaded / e.total) * 100) }) });
        patch(key, { pct: 100, status: "done" }); done.push(data);
      } catch (e) { patch(key, { status: "error", error: formatApiError(e.response?.data?.detail) || "Errore" }); }
    }
    setTimeout(() => setUploads((u) => u.filter((x) => !items.some((it) => it.key === x.key && x.status === "done"))), 1500);
    reload();
    if (done.length) setRenameQueue((rq) => [...rq, ...done]);
  };
  useEffect(() => { if (!renaming && renameQueue.length) { setRenaming(renameQueue[0]); setRenameQueue((rq) => rq.slice(1)); } }, [renaming, renameQueue]);

  const saveName = async (name) => {
    const f = renaming;
    setRenaming(null);
    if (name !== f.nome) { try { await api.patch(`/studio/allegati-studio/files/${f.id}`, { nome: name }); loadFiles(); } catch (e) { err(e); } }
  };
  const move = (f, folder_id) => api.patch(`/studio/allegati-studio/files/${f.id}`, { folder_id }).then(() => { toast.success("File spostato"); reload(); }).catch(err);
  const askDelete = (kind, it) => setConfirm({ kind, it, text: kind === "folder"
    ? `Eliminare la cartella "${it.name}" e i ${it.count} file che contiene? L'operazione non è reversibile.`
    : `Eliminare il file "${it.nome}"? L'operazione non è reversibile.` });
  const doDelete = async () => {
    const { kind, it } = confirm; setConfirm(null);
    try {
      await api.delete(`/studio/allegati-studio/${kind === "folder" ? "folders" : "files"}/${it.id}`);
      if (kind === "folder" && current === it.id) setCurrent("");
      toast.success("Eliminato"); reload();
    } catch (e) { err(e); }
  };

  const title = q ? `Risultati per "${q}"` : current ? folders.find((f) => f.id === current)?.name || "Cartella" : "Radice";
  return (
    <div data-testid="allegati-studio-page">
      <PageHeader title="Allegati Studio" subtitle="Archivio dei file dello studio, organizzato in cartelle." icon={Paperclip} testId="allegati-studio-header"
        actions={canWrite && (<>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="sf-upload-input" onChange={(e) => { const l = [...(e.target.files || [])]; e.target.value = ""; if (l.length) uploadAll(l); }} />
          <Button onClick={() => fileRef.current?.click()} data-testid="sf-upload-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold"><Upload className="h-4 w-4 mr-1" />Carica file</Button>
        </>)} />
      <div className="relative mb-5 pgp-fade-up">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca file per nome in tutte le cartelle…" data-testid="sf-search-input"
          className="w-full h-11 rounded-lg border border-slate-700 bg-transparent pl-10 pr-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500 transition-colors" />
      </div>
      <div className="grid gap-5 lg:grid-cols-[var(--side-w)_minmax(0,1fr)]" style={{ "--side-w": `${sideW}px` }}>
        <aside className="relative pgp-card p-3 h-fit space-y-3">
          <ResizeHandle width={sideW} onChange={setSideW} testId="allegati-side-resize-handle" className="hidden lg:block" />
          <div className="text-[11px] uppercase tracking-widest font-mono text-slate-500 px-1">Cartelle</div>
          <SfFolderList folders={folders} rootCount={rootCount} current={q ? null : current} canWrite={canWrite}
            onSelect={(id) => { setQ(""); setCurrent(id); }} onRename={renameFolder} onDelete={(f) => askDelete("folder", f)} onReorder={reorder} />
          {canWrite && (
            <div className="flex gap-2 pt-2 border-t border-slate-800">
              <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="Nuova cartella" data-testid="sf-new-folder-input"
                className="flex-1 min-w-0 h-9 rounded-lg border border-slate-700 bg-transparent px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500" />
              <Button size="sm" onClick={createFolder} disabled={!newFolder.trim()} data-testid="sf-new-folder-button" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14]"><FolderPlus className="h-4 w-4" /></Button>
            </div>
          )}
        </aside>
        <section className="space-y-4 min-w-0">
          <SfUploadProgress items={uploads} />
          <div className="flex items-center justify-between"><h2 className="font-head font-semibold text-slate-100 text-base md:text-lg truncate" data-testid="sf-current-title">{title}</h2>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs font-mono text-slate-500" data-testid="sf-file-count">{files.length} file</span>
              <div className="flex rounded-lg border border-slate-700 overflow-hidden" data-testid="sf-view-toggle">
                {[["grid", LayoutGrid, "Griglia"], ["list", List, "Lista"]].map(([k, I, l]) => (
                  <button key={k} type="button" title={l} onClick={() => setView(k)} data-testid={`sf-view-${k}`}
                    className={`p-1.5 transition-colors ${view === k ? "bg-amber-500 text-[#0a0d14]" : "text-slate-400 hover:bg-slate-800/60"}`}><I className="h-4 w-4" /></button>
                ))}
              </div>
            </div></div>
          {loading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
            : files.length === 0 ? <div className="pgp-card py-14 text-center text-sm text-slate-500" data-testid="sf-empty">{q ? "Nessun file trovato." : "Nessun file in questa posizione. Usa \"Carica file\" per aggiungerne."}</div>
            : view === "list" ? (
              <div className="pgp-card overflow-hidden" data-testid="sf-file-list">
                {files.map((f) => <SfFileCard key={f.id} list f={f} folders={folders} canWrite={canWrite} onOpen={setPreview} onMove={move} onRename={(x) => setRenaming({ ...x, manual: true })} onDelete={(x) => askDelete("file", x)} />)}
              </div>
            ) : (
              <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 xl:grid-cols-5" data-testid="sf-file-grid">
                {files.map((f) => <SfFileCard key={f.id} f={f} folders={folders} canWrite={canWrite} onOpen={setPreview} onMove={move} onRename={(x) => setRenaming({ ...x, manual: true })} onDelete={(x) => askDelete("file", x)} />)}
              </div>
            )}
        </section>
      </div>
      <SfRenameDialog item={renaming} title={renaming?.manual ? "Rinomina file" : "Caricamento completato"} onSave={saveName} onClose={() => setRenaming(null)} />
      <SfPreviewDialog file={preview} onClose={() => setPreview(null)} />
      <SfConfirmDialog text={confirm?.text} onConfirm={doDelete} onClose={() => setConfirm(null)} />
    </div>
  );
}
